import { Check, CheckCircle2, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import MobileShell, { BrandFooter } from '../components/MobileShell.jsx';

export default function QuizPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(location.state?.attempt || null);
  const [selected, setSelected] = useState(location.state?.attempt?.feedback?.selected || []);
  const [feedback, setFeedback] = useState(location.state?.attempt?.feedback || null);
  const [completedAfterFeedback, setCompletedAfterFeedback] = useState(Boolean(location.state?.attempt?.completed));
  const [remaining, setRemaining] = useState(location.state?.attempt?.remainingMs ?? 0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const submitting = useRef(false);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    const id = location.state?.attempt?.attemptId || sessionStorage.getItem('activeAttemptId');
    if (!id) { navigate('/', { replace: true }); return; }
    api(`/api/quiz/${id}`).then((data) => {
      if (data.completed) navigate(`/result/${id}`, { replace: true });
      else {
        setAttempt(data);
        if (data.reviewing) {
          setFeedback(data.feedback);
          setSelected(data.feedback.selected);
        } else {
          setFeedback(null);
        }
      }
    }).catch(() => navigate('/', { replace: true }));
  }, [location.state?.attempt?.attemptId, navigate]);

  useEffect(() => {
    if (!attempt?.question || feedback) return undefined;
    startedAt.current = Date.now();
    setRemaining(attempt.remainingMs);
    const timer = window.setInterval(() => {
      setRemaining((value) => Math.max(0, value - 100));
    }, 100);
    return () => window.clearInterval(timer);
  }, [attempt?.question?.id, attempt?.remainingMs, feedback]);

  const submitAnswer = useCallback(async (forcedSelection = selected, timedOut = false) => {
    if (!attempt || submitting.current) return;
    submitting.current = true;
    setLoading(true);
    setError('');
    try {
      const next = await api(`/api/quiz/${attempt.attemptId}/answer`, {
        method: 'POST',
        body: JSON.stringify({ selected: forcedSelection, timedOut, elapsedMs: Date.now() - startedAt.current }),
      });
      setFeedback(next.feedback);
      setCompletedAfterFeedback(next.completed);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  }, [attempt, selected]);

  const continueAfterFeedback = useCallback(async () => {
    if (!attempt || loading) return;
    if (completedAfterFeedback) {
      sessionStorage.removeItem('activeAttemptId');
      navigate(`/result/${attempt.attemptId}`, { replace: true });
      return;
    }
    setLoading(true);
    setError('');
    try {
      const next = await api(`/api/quiz/${attempt.attemptId}/next`, { method: 'POST', body: '{}' });
      setRemaining(next.remainingMs);
      setSelected([]);
      setAttempt(next);
      setFeedback(null);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [attempt, completedAfterFeedback, loading, navigate]);

  useEffect(() => {
    if (attempt?.question && !feedback && remaining === 0 && !submitting.current) submitAnswer([], true);
  }, [remaining, attempt?.question, feedback, submitAnswer]);

  if (!attempt?.question) return <div className="screen-loader">正在载入题目…</div>;
  const question = attempt.question;
  const isMultiple = question.type === 'multiple';
  const progress = (attempt.currentIndex + 1) / attempt.total;
  const seconds = Math.ceil(remaining / 1000);
  const timerProgress = feedback ? 0 : Math.max(0, Math.min(100, remaining / (question.timeLimit * 10)));

  function choose(key) {
    if (loading || feedback) return;
    setSelected((current) => isMultiple
      ? current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
      : [key]);
  }

  return (
    <MobileShell className={`quiz-page${feedback ? ' showing-feedback' : ''}`}>
      <div className="quiz-progress-track"><span style={{ width: `${progress * 100}%` }} /></div>
      <section className="quiz-card">
        <header className="quiz-card-head">
          <div className="question-type">{isMultiple ? '多选题' : '单选题'}<small>{isMultiple ? '可选择多个答案' : '请选择一个答案'}</small></div>
          <div className="question-counter"><b>{String(attempt.currentIndex + 1).padStart(2, '0')}</b> / {attempt.total} 题</div>
        </header>
        <div className={`quiz-timer ${seconds <= 3 && !feedback ? 'urgent' : ''}`} aria-label={feedback ? '本题作答结束' : `剩余 ${seconds} 秒`}>
          <strong>{feedback ? '—' : String(seconds).padStart(2, '0')}</strong>
          <svg viewBox="0 0 36 36" aria-hidden="true">
            <circle className="timer-track" cx="18" cy="18" r="14" />
            <circle className="timer-progress" cx="18" cy="18" r="14" pathLength="100" strokeDasharray={`${timerProgress} 100`} />
          </svg>
        </div>
        <div className="question-content">
          <h1>{question.stem}</h1>
          <div className="answer-list">
            {question.options.map((option) => {
              const active = selected.includes(option.key);
              const isCorrectAnswer = feedback?.answer.includes(option.key);
              const isWrongSelection = Boolean(feedback && active && !isCorrectAnswer);
              const stateClass = isCorrectAnswer ? ' correct-answer' : isWrongSelection ? ' wrong-answer' : active ? ' active' : '';
              return (
                <button key={option.key} type="button" className={`answer-option${stateClass}`} onClick={() => choose(option.key)} disabled={Boolean(feedback)}>
                  <span className="option-key">{active || isCorrectAnswer ? <Check size={17} /> : option.key}</span>
                  <span>{option.text}</span>
                </button>
              );
            })}
          </div>
          {feedback && (
            <section className={`answer-feedback ${feedback.correct ? 'correct' : 'incorrect'}`} aria-live="polite">
              <header>{feedback.correct ? <CheckCircle2 size={20} /> : <XCircle size={20} />}<b>{feedback.correct ? '回答正确' : feedback.timedOut ? '作答超时' : '回答错误'}</b></header>
              <div><span>正确答案</span><strong>{feedback.answer.join('、')}</strong></div>
              <p><b>答案解析</b>{feedback.explanation || '本题暂无解析。'}</p>
            </section>
          )}
          {error && <div className="form-error">{error}</div>}
        </div>
        <button className="next-button" type="button" disabled={loading || (!feedback && selected.length === 0)} onClick={feedback ? continueAfterFeedback : () => submitAnswer()}>{feedback ? completedAfterFeedback ? '查看成绩' : '下一题' : loading ? '正在判题…' : '确认答案'}</button>
      </section>
      <img className="quiz-side-character" src="/assets/6.png" alt="" />
      <BrandFooter />
    </MobileShell>
  );
}
